'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { OrganisationSwitchWarningDialog } from '@workspace/ui';
import { cn, trackEvent, USER_ROLES } from '@workspace/ui/lib';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { updateNotificationReadStatus } from '@/actions/notification';
import NotificationIcon from '@/components/notifications/notification-icon';
import {
  type PendingNotificationContextSwitch,
  useNotificationContextSwitch,
} from '@/hooks/use-notification-context-switch';
import { getActiveOrganisationFromUser } from '@/lib/active-organisation';
import { getEorHref } from '@/lib/eor-app-url';
import { getPayrollToRunNotificationHref } from '@/lib/notifications';
import { useAuthStore } from '@/stores/authStore';
import type { NotificationItem as NotificationItemType } from '@/types/notifications';

function getMetadataRouteValue(
  metadata: NotificationItemType['metadata'],
  key: string
): string | null {
  const value = metadata?.[key];

  if (typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'string' && value.trim()) {
    return value;
  }

  return null;
}

export default function NotificationItem({ item }: { item: NotificationItemType }) {
  const queryClient = useQueryClient();
  const { push } = useRouter();
  const currentUserId = useAuthStore((state) => state.user?.user?.id);
  const userRoles = useAuthStore((state) => state.user?.roles);
  const activeOrganisationId = useAuthStore(
    (state) => getActiveOrganisationFromUser(state.user)?.id
  );

  const { prepareContextSwitch, switchContext, isSwitchingContext } =
    useNotificationContextSwitch();
  const [pendingContextSwitch, setPendingContextSwitch] = useState<
    (PendingNotificationContextSwitch & { href: string }) | null
  >(null);

  const markAsReadMutation = useMutation({
    mutationFn: () =>
      updateNotificationReadStatus({
        notification_id: item.id,
        notification_user_id: [item.notification_user_id],
        read: true,
      }),
    onError: (err) => {
      console.error('Failed to mark notification as read:', err);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  function getNotificationHref(notification: NotificationItemType): string | null {
    const payrollToRunHref = getPayrollToRunNotificationHref(notification);
    if (payrollToRunHref) {
      return getEorHref(payrollToRunHref);
    }

    const msg = notification.message.toLowerCase();

    switch (notification.key) {
      case 'carry_over_days_expiring':
        return getEorHref('/absences');
      case 'pending_absence_request': {
        const isGlobalAdminOrEmployer =
          (userRoles?.includes(USER_ROLES.TEAMS_GLOBAL_ADMIN) ||
            userRoles?.includes(USER_ROLES.EMPLOYER)) ??
          false;
        if (isGlobalAdminOrEmployer) {
          const orgId =
            getMetadataRouteValue(notification.metadata, 'organisation_id') ??
            (activeOrganisationId != null ? String(activeOrganisationId) : null);
          return orgId
            ? getEorHref(`/absences/organisation/${encodeURIComponent(orgId)}?tab=pending`)
            : getEorHref('/absences?tab=pending');
        }
        const teamId = getMetadataRouteValue(notification.metadata, 'team_id');
        return teamId
          ? getEorHref(`/absences/team/${encodeURIComponent(teamId)}?tab=pending`)
          : getEorHref('/absences?tab=pending');
      }
      case 'absence_request_approved': {
        const userId =
          getMetadataRouteValue(notification.metadata, 'user_id') ??
          (currentUserId != null ? String(currentUserId) : null);

        return userId
          ? getEorHref(`/absences/employee/${encodeURIComponent(userId)}?tab=approved`)
          : getEorHref('/absences');
      }
      case 'absence_request_rejected': {
        const userId =
          getMetadataRouteValue(notification.metadata, 'user_id') ??
          (currentUserId != null ? String(currentUserId) : null);

        return userId
          ? getEorHref(`/absences/employee/${encodeURIComponent(userId)}?tab=rejected`)
          : getEorHref('/absences');
      }
      default:
        break;
    }

    switch (notification.type) {
      case 'teams':
      case 'team_members':
        return getEorHref('/people');
      case 'invoices':
        return getEorHref('/billing');
      case 'settings':
      case 'organisation':
      case 'kyb':
      case 'msa':
        return getEorHref('/settings');
      case 'task':
        return getEorHref('/employment');
      case 'expenses': {
        const expenseId = getMetadataRouteValue(notification.metadata, 'expense_id');
        if (expenseId) {
          return `/expenses/${encodeURIComponent(expenseId)}`;
        }
        return '/expenses';
      }
      case 'payroll': {
        if (msg.includes('payslip')) {
          return getEorHref('/employment?tab=documents&docType=payslip');
        }

        return getEorHref('/payroll');
      }
      default:
        break;
    }

    if (msg.includes('payslip')) {
      return getEorHref('/employment?tab=documents&docType=payslip');
    }

    return null;
  }

  async function handleNotificationClick() {
    trackEvent('message_click', 'notification_item', {
      notification_id: item.id.toString(),
      notification_type: item.type,
      notification_key: item.key,
      notification_read: item.read,
      notification_message: item.message,
    });

    const href = getNotificationHref(item);

    // Opening the page needs another organisation / role: ask first, then switch (see
    // `confirmContextSwitch`). The notification is marked read once the user confirms.
    const contextSwitch = href ? await prepareContextSwitch(item) : null;
    if (href && contextSwitch) {
      setPendingContextSwitch({ ...contextSwitch, href });
      return;
    }

    if (!item.read) {
      markAsReadMutation.mutate();
    }

    if (href) {
      if (/^https?:\/\//.test(href)) {
        window.location.assign(href);
      } else {
        push(href);
      }
    }
  }

  async function confirmContextSwitch() {
    if (!pendingContextSwitch) return;

    const { target, href } = pendingContextSwitch;
    const markAsReadPromise = item.read
      ? undefined
      : markAsReadMutation.mutateAsync().catch(() => undefined);

    const didSwitchContext = await switchContext(target);

    if (didSwitchContext) {
      await markAsReadPromise;
      // Full load so the new session cookies and auth state are picked up.
      window.location.assign(href);
      return;
    }

    // Switch failed (e.g. SEAT_NOT_HELD): stay in the current context and open the page as before.
    setPendingContextSwitch(null);
    if (/^https?:\/\//.test(href)) {
      window.location.assign(href);
    } else {
      push(href);
    }
  }

  return (
    <>
      <button
        type="button"
        disabled={markAsReadMutation.isPending || isSwitchingContext}
        className={cn(
          'group hover:bg-grey-50 dark:hover:bg-grey-900 relative flex w-full cursor-pointer items-start gap-3 rounded-2xl p-3 pr-5.5 text-left'
        )}
        onClick={() => void handleNotificationClick()}
        aria-label={`${!item.read ? 'Unread' : 'Read'} notification: ${item.message}`}
      >
        <NotificationIcon type={item.type} />

        <div className="flex max-w-[359px] flex-col gap-2">
          <p className="text-grey-1000 group-hover:text-neutral-1000 pointer-events-none dark:text-neutral-100">
            {item.message}
          </p>

          <p
            className={cn(
              'text-grey-900/50 group-hover:text-grey-40 pointer-events-none text-xs dark:text-neutral-100/50',
              !item.read && 'text-grey-700'
            )}
          >
            {item.date}
          </p>
        </div>

        {!item.read && (
          <div
            className="bg-secondary absolute top-3 right-3 h-2 w-2 rounded-full"
            aria-hidden="true"
          ></div>
        )}
      </button>
      <OrganisationSwitchWarningDialog
        open={pendingContextSwitch != null}
        onOpenChange={(open) => {
          if (!open && !isSwitchingContext) setPendingContextSwitch(null);
        }}
        onConfirm={() => void confirmContextSwitch()}
        viewName={pendingContextSwitch?.viewName ?? ''}
        isConfirming={isSwitchingContext}
      />
    </>
  );
}
